import { useState } from "react";
import { Banknote } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/core/api/apiTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { AmountInput } from "@/shared/components/ui/AmountInput";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { AppointmentChargeGuard } from "../components/AppointmentChargeGuard";
import { paymentKeys, usePaymentMutations } from "../hooks/paymentQueries";
import {
  formatPaymentMoney, parsePaymentAmount, paymentAmountInput, paymentCurrencyDigits,
  RecordCashPaymentRequestSchema, type PaymentSummary, type RecordCashPaymentRequest,
} from "../types/paymentTypes";

// An unconfirmed cash entry survives reloads so a retry replays the same key instead of recording twice.
function restoreAttempt(storageKey: string, currency: string): RecordCashPaymentRequest | null {
  try {
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) return null;
    const attempt = RecordCashPaymentRequestSchema.parse(JSON.parse(saved));
    return attempt.expectedCurrency === currency ? attempt : null;
  } catch { return null; }
}
function storeAttempt(storageKey: string, attempt: RecordCashPaymentRequest | null) {
  try {
    if (attempt) sessionStorage.setItem(storageKey, JSON.stringify(attempt));
    else sessionStorage.removeItem(storageKey);
  } catch { /* The in-memory attempt still protects retries in this page. */ }
}

function CashForm({ appointmentId, businessId, userId, summary }: {
  appointmentId: string; businessId: string; userId: string; summary: PaymentSummary;
}) {
  const { recordCash } = usePaymentMutations(appointmentId);
  const queryClient = useQueryClient();
  const capture = useWorkspaceOperation();
  const { goToAppointmentDetails } = useAppNavigation();
  const storageKey = JSON.stringify(["cashPaymentAttempt", userId, businessId, appointmentId]);
  const [restored] = useState(() => restoreAttempt(storageKey, summary.currency));
  const [attempt, setAttempt] = useState(restored);
  const [amount, setAmount] = useState(() =>
    paymentAmountInput(restored?.amountMinor ?? summary.balanceMinor ?? 0, summary.currency));
  const amountMinor = parsePaymentAmount(amount, summary.currency);
  const amountError = amountMinor === null ? `Enter a positive amount${summary.currency === "JPY" ? " with no decimal places" : " with up to 2 decimal places"}.` :
    amountMinor > (summary.balanceMinor ?? 0) ? "The amount cannot exceed the amount due." : undefined;
  const busy = recordCash.isPending;
  const locked = busy || !!attempt;
  function submit() {
    if (busy) return;
    if (!attempt && (amountError || !amountMinor)) return;
    const request = attempt ?? { amountMinor: amountMinor!, expectedCurrency: summary.currency, idempotencyKey: crypto.randomUUID() };
    setAttempt(request);
    storeAttempt(storageKey, request);
    const isCurrent = capture();
    recordCash.mutate(request, {
      onSuccess: () => {
        storeAttempt(storageKey, null);
        if (isCurrent()) goToAppointmentDetails(appointmentId, true);
      },
      onError: error => {
        // A rejected request recorded nothing, so the owner may correct the amount and try again.
        if (error instanceof ApiError && (error.Status === 409 || error.Status === 400)) {
          storeAttempt(storageKey, null);
          if (!isCurrent()) return;
          setAttempt(null);
          void queryClient.invalidateQueries({ queryKey: paymentKeys.appointment(businessId, appointmentId, userId) });
        }
      },
    });
  }
  return <div className="space-y-4">
    <p className="text-sm text-muted-foreground">Amount due {formatPaymentMoney(summary.balanceMinor ?? 0, summary.currency)}.</p>
    {attempt && !busy && !recordCash.error && restored === attempt &&
      <p className="text-sm text-muted-foreground">An earlier cash entry was not confirmed. Retrying uses the same entry and never records it twice.</p>}
    <fieldset disabled={locked} className="min-w-0">
      <AmountInput label={`Cash received (${summary.currency})`} value={amount} onValueChange={setAmount} error={attempt ? undefined : amountError} />
    </fieldset>
    {recordCash.error && <ErrorState message={recordCash.error.message} />}
    <Button className="w-full" disabled={busy || (!attempt && !!amountError)} onClick={submit}>
      <Banknote aria-hidden="true" />
      {attempt && (recordCash.error || restored === attempt) ? "Retry" :
        `Record ${formatPaymentMoney(attempt?.amountMinor ?? amountMinor ?? 0, summary.currency)} cash`}
    </Button>
  </div>;
}

export function AppointmentCashChargePage() {
  return <AppointmentChargeGuard title="Cash">
    {({ appointmentId, summary, businessId, userId }) =>
      !summary.canRecordCash || paymentCurrencyDigits(summary.currency) === null ?
        <ErrorState message={summary.cashUnavailableReason ?? "Cash cannot be recorded for this appointment."} /> :
        <CashForm key={`${userId}:${businessId}:${appointmentId}:${summary.currency}`} appointmentId={appointmentId}
          businessId={businessId} userId={userId} summary={summary} />}
  </AppointmentChargeGuard>;
}
